import React, {useEffect, useState} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router';
import {useQuery} from 'react-query';
import {connect} from 'react-redux';
import {ChevronRight, CircleUserRound} from 'lucide-react';
import {
  DashboardLayout,
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
} from 'components';
import PageNumber from 'components/PageNumber';
import Breadcrumbs from 'components/BreadCrumbs';
import {PAGES_URL} from 'helpers/constants';
import {fetchSearchDetails, fetchSearchProfiles} from 'requests/queries/aml';
import AMLSidebar from './AMLSidebar';
import {useAMLContext} from 'helpers/context/AMLContext';
import * as Actions from 'store/actions';
import {corruptedFile} from 'assets/images/images';

function AMLScreeningResults({team = {}, getTeamMembers}) {
  const navigate = useNavigate();
  const {id: searchId} = useParams();
  const {state} = useLocation();
  const {setSearchDetails, customerName, setCustomerName} = useAMLContext();
  const members = team?.members || {team: []};
  const [paginationQuery, setPaginationQuery] = useState({
    page: 1,
    length: 10,
  });

  useEffect(() => {
    if (state?.customerName) {
      setCustomerName(
        typeof state.customerName === 'string'
          ? state.customerName.replaceAll('_', ' ')
          : 'Unknown',
      );
    }
  }, [state?.customerName, setCustomerName]);

  useEffect(() => {
    getTeamMembers(`status=active`);
  }, [getTeamMembers]);

  useEffect(() => {
    setPaginationQuery(prev => ({...prev, page: 1}));
  }, [searchId]);

  const {
    data: searchProfiles,
    isLoading: isProfilesLoading,
    error: profilesError,
  } = useQuery({
    queryKey: [
      'searchProfiles',
      searchId,
      paginationQuery.page,
      paginationQuery.length,
    ],
    queryFn: () =>
      fetchSearchProfiles({
        id: searchId,
        page: paginationQuery.page,
        limit: paginationQuery.length,
      }),
    enabled: !!searchId,
  });

  const {isLoading: isSearchLoading, error: searchError} = useQuery({
    queryKey: ['searchDetails', searchId],
    queryFn: () => fetchSearchDetails({id: searchId}),
    enabled: !!searchId,
    onSuccess: data => {
      setSearchDetails(data);
      const newCustomerName =
        data?.customerName ||
        data?.entity?.search_query ||
        customerName ||
        'Unknown';
      setCustomerName(
        typeof newCustomerName === 'string'
          ? newCustomerName.replaceAll('_', ' ')
          : 'Unknown',
      );
    },
    cacheTime: 0,
    staleTime: 0,
    retry: false,
  });

  const getMatchStatusStyles = matchStatus => {
    const status = matchStatus?.toLowerCase() || 'unknown';
    const statusMap = {
      'confirmed match': {
        bg: 'bg-[#FBF4F4]',
        text: 'text-[#B42318]',
        display: 'Confirmed match',
      },
      'partial match': {
        bg: 'bg-[#F2F6FD]',
        text: 'text-[#3F7CDB]',
        display: 'Partially matched',
      },
      'potential match': {
        bg: 'bg-[#F0FFFE]',
        text: 'text-[#17A6A0]',
        display: 'Potential match',
      },
      'no match': {bg: 'bg-[#F7F7F7]', text: 'text-black', display: 'No match'},
    };

    return (
      statusMap[status] || {
        bg: 'bg-disabled',
        text: 'text-gray-700',
        display: 'Unknown',
      }
    );
  };

  const getProfileTypeStyles = profileType => {
    const type = profileType?.toLowerCase() || 'unknown';
    const typeMap = {
      pep: {bg: 'bg-[#E2F5F6]', text: 'text-[#4A878D]'},
      adverse_media: {bg: 'bg-[#D9E5F8]', text: 'text-[#2A5392]'},
      sanction: {bg: 'bg-[#F0D3D1]', text: 'text-[#B42318]'},
    };

    return typeMap[type] || {bg: 'bg-[#F7F7F7]', text: 'text-gray-700'};
  };

  return (
    <DashboardLayout
      bg="bg-white80"
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">AML Screening</p>
      }
    >
      <div className="flex items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E3E6]">
        <Breadcrumbs
          items={[
            {label: 'AML Screening', path: PAGES_URL.AML_SCREENING},
            {label: 'AML Screening Results'},
          ]}
        />
      </div>
      <div className="flex flex-col lg:flex-row w-full pt-3 gap-[54px] items-start">
        <div className="lg:w-[67%] w-full">
          {isProfilesLoading ? (
            <Loader height={50} />
          ) : profilesError ? (
            <div className="text-center text-red-500 py-4" role="alert">
              Error: {profilesError?.message || 'Failed to load profiles.'}
            </div>
          ) : (
            <>
              <p className="text-[12px] font-normal text-[#858DAA] flex items-center gap-1.5 mb-1">
                Showing results for{' '}
                <span className="bg-[#B42318] rounded-full h-5 w-5 flex items-center justify-center text-white text-[10px] p-1">
                  {searchProfiles?.total || 0}
                </span>
              </p>
              <p className="text-base text-deepBlue font-medium capitalize">
                {customerName || 'Unknown'}
              </p>
              <div className="mt-5">
                {!searchProfiles?.profiles?.length ? (
                  <EmptyState
                    height={35}
                    body={'No results found for this entity'}
                    src={corruptedFile}
                    noBtn
                  />
                ) : (
                  searchProfiles.profiles.map(profile => {
                    const {
                      bg: matchBg,
                      text: matchText,
                      display,
                    } = getMatchStatusStyles(profile.match_status);
                    const {bg: typeBg, text: typeText} = getProfileTypeStyles(
                      profile.profile_type,
                    );
                    const displayType = (() => {
                      const typeLower = profile.profile_type?.toLowerCase();
                      if (typeLower === 'sanction')
                        return 'SANCTIONS & WARNINGS';
                      if (typeLower === 'adverse_media') return 'ADVERSE MEDIA';
                      if (typeLower === 'pep') return 'PEP';
                      return profile.profile_type;
                    })();
                    return (
                      <div
                        key={profile.id}
                        className="rounded-lg border border-[#D9E5F8] mb-4"
                      >
                        <div className="py-4 px-5 bg-white rounded-t-lg grid grid-cols-[40%_40%_20%]">
                          <div className="flex items-center gap-2">
                            <div className="p-[5px] rounded-full bg-[#EEF3FC] w-fit">
                              <CircleUserRound color="#3F7CDB" size={18} />
                            </div>
                            <h1 className="text-[12px] lg:text-tiny font-medium text-[#080917]">
                              {profile.profile_name}
                            </h1>
                          </div>
                          <div>
                            <p className="text-[#080917] text-[10px] font-normal uppercase mb-2">
                              Match status
                            </p>
                            <div
                              className={`py-1 px-2 rounded-full w-fit ${matchBg} ${matchText} text-[10px] uppercase font-normal`}
                            >
                              {display}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[#080917] text-[10px] font-normal uppercase mb-2">
                              Profile Category
                            </p>
                            <p className="text-deepBlue font-medium text-[10px] uppercase break-words">
                              {profile.profile_category
                                ? Array.isArray(profile.profile_category)
                                  ? profile.profile_category
                                      .map(category =>
                                        category.replace(/_/g, ' '),
                                      )
                                      .join(', ')
                                  : String(profile.profile_category).replace(
                                      /_/g,
                                      ' ',
                                    )
                                : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="py-[10px] px-5 bg-[#edf3fd] rounded-b-lg flex items-center justify-between w-full">
                          <div className="flex items-center gap-1">
                            <div
                              className={`py-1 px-2 rounded-full w-fit ${typeBg} ${typeText} text-[10px] uppercase font-normal`}
                            >
                              {displayType}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              navigate(
                                `/aml-screening/v2/single-result/${profile.id}`,
                                {
                                  state: {
                                    searchProfiles,
                                    customerName: customerName || 'Unknown',
                                    searchId: searchId,
                                    profileId: profile.id,
                                  },
                                },
                              );
                            }}
                            className="flex items-center gap-1 text-[10px] font-medium uppercase"
                          >
                            View more info
                            <ChevronRight size={12} color="#0B1C56" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}

          {searchProfiles?.profiles?.length > 0 && !isProfilesLoading && (
            <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mt-6 py-[18px] px-6 border-[0.5px] border-y-[#E4E3EC] border-x-transparent">
              <PageNumber
                currentPage={paginationQuery.page}
                limit={paginationQuery.length}
                totalCount={searchProfiles?.total || 0}
              />
              <div className="flex items-center gap-5">
                <PageLimit
                  onLimitChange={limit =>
                    setPaginationQuery(prev => ({
                      ...prev,
                      length: limit,
                      page: 1,
                    }))
                  }
                  total={searchProfiles?.total || 0}
                  length={searchProfiles?.profiles?.length || 0}
                  limit={paginationQuery.length}
                />
                <Pagination
                  total={
                    searchProfiles?.total
                      ? Math.ceil(searchProfiles.total / paginationQuery.length)
                      : 1
                  }
                  current={paginationQuery.page}
                  onPageChange={page =>
                    setPaginationQuery(prev => ({...prev, page}))
                  }
                />
              </div>
            </div>
          )}
        </div>
        <AMLSidebar
          isSearchLoading={isSearchLoading}
          searchError={searchError}
          members={members}
        />
      </div>
    </DashboardLayout>
  );
}

export default connect(
  state => ({
    team: state.team,
  }),
  {getTeamMembers: Actions.getTeamMembers},
)(AMLScreeningResults);
